const MAINNET_ALPHA_RELAY_QUERY_URL = 'https://relay-mainnet.alpha.ducatprotocol.com/api/query';
const MAINNET_ALPHA_RELAY_PROXY_PATH = '/api/oracle-relay/mainnet-alpha/query';

function applicationOrigin(): string {
  return typeof window === 'undefined' ? 'https://alpha.ducatprotocol.com' : window.location.origin;
}

export function getMainnetAlphaRelayFallbackUrl(url: string): string | null {
  const parsed = new URL(url, applicationOrigin());
  if (parsed.href !== MAINNET_ALPHA_RELAY_QUERY_URL) return null;
  return `${applicationOrigin()}${MAINNET_ALPHA_RELAY_PROXY_PATH}`;
}

function timedSignal(milliseconds: number, parent?: AbortSignal | null) {
  const controller = new AbortController();
  const abort = () => controller.abort(parent?.reason);
  const timer = setTimeout(() => controller.abort(new Error('Oracle relay request timed out')), milliseconds);
  if (parent?.aborted) abort();
  else parent?.addEventListener('abort', abort, { once: true });
  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timer);
      parent?.removeEventListener('abort', abort);
    },
  };
}

export async function fetchRelayQueryWithFallback(url: string, init: RequestInit): Promise<unknown> {
  const deadline = timedSignal(15_000, init.signal);
  const fallbackUrl = getMainnetAlphaRelayFallbackUrl(url);
  const direct = fallbackUrl ? timedSignal(5_000, deadline.signal) : deadline;
  const read = async (target: string, signal: AbortSignal) => {
    if (signal.aborted) throw signal.reason;
    const response = await fetch(target, { ...init, signal });
    if (!response.ok) throw new Error(`Oracle relay returned ${response.status}`);
    return response.json();
  };
  try {
    try {
      return await read(url, direct.signal);
    } catch (error) {
      if (!fallbackUrl || deadline.signal.aborted) throw error;
      direct.dispose();
      return await read(fallbackUrl, deadline.signal);
    }
  } finally {
    direct.dispose();
    deadline.dispose();
  }
}
